import { useState, useCallback, useRef } from 'react';
import { scanAPI } from '../api';
import BarcodeScanner from './BarcodeScanner';

// ─── SHARED STYLES ────────────────────────────────────────────────────────

const card = {
  background: '#1e293b',
  borderRadius: 16,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.07)',
  marginBottom: 14,
};

const btn = (bg, disabled) => ({
  padding: '13px 16px', borderRadius: 10, border: 'none', fontWeight: 700,
  fontSize: 15, cursor: disabled ? 'not-allowed' : 'pointer',
  background: disabled ? '#334155' : bg, color: '#fff', width: '100%',
  opacity: disabled ? 0.6 : 1, transition: 'opacity .15s',
});

const inp = {
  width: '100%', padding: '12px 14px', borderRadius: 9, outline: 'none',
  background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
  color: '#fff', fontSize: 15, boxSizing: 'border-box',
};

function Tag({ color, children }) {
  return (
    <span style={{
      background: color + '22', border: `1px solid ${color}55`,
      color, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700
    }}>{children}</span>
  );
}

function ManualEntry({ onAdd, btnColor }) {
  const [val, setVal] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <input
        style={{ ...inp, flex: 1 }}
        value={val}
        onChange={e => setVal(e.target.value.toUpperCase())}
        onKeyDown={e => { if (e.key === 'Enter' && val) { onAdd(val); setVal(''); } }}
        placeholder="Type barcode manually"
      />
      <button
        onClick={() => { if (val) { onAdd(val); setVal(''); } }}
        style={{
          padding: '12px 16px', borderRadius: 9, border: 'none',
          background: btnColor || '#3b82f6', color: '#fff',
          fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'
        }}
      >Add</button>
    </div>
  );
}

// ─── RECEIVING PAGE ───────────────────────────────────────────────────────
export function ReceivingPage({ user }) {
  const [lr, setLr] = useState('');
  const [lrSet, setLrSet] = useState(false);
  const [batch, setBatch] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const addBarcode = useCallback(async (code) => {
    const barcode = code.trim().toUpperCase();

    if (!barcode.startsWith('WH')) {
      setBatch(p => [{ barcode, status: 'error', msg: '🚫 Invalid — only WH barcodes accepted' }, ...p.filter(b => b.barcode !== barcode)]);
      return;
    }
    if (batch.find(b => b.barcode === barcode)) {
      setBatch(p => [{ barcode, status: 'dup', msg: '⚠️ Already in this batch' }, ...p.filter(b => b.barcode !== barcode)]);
      return;
    }
    try {
      const res = await scanAPI.validate(barcode);
      if (res.data.exists) {
        setBatch(p => [{ barcode, status: 'dup', msg: `⚠️ Already received (${res.data.receivedDate})` }, ...p]);
        return;
      }
    } catch {}
    setBatch(p => [{ barcode, status: 'ok', msg: '✅ Ready to submit' }, ...p]);
  }, [batch]);

  const submit = async () => {
    const valid = batch.filter(b => b.status === 'ok').map(b => b.barcode);
    if (!valid.length) return;
    setSubmitting(true);
    try {
      const res = await scanAPI.receive(lr, valid, valid.length);
      setSubmitted(res.data);
      setBatch(p => p.map(b => {
        const err = res.data.errors?.find(e => e.barcode === b.barcode);
        return { ...b, status: err ? 'error' : 'saved', msg: err ? `❌ ${err.error}` : '✅ Saved to database' };
      }));
    } catch (err) {
      alert('Submit failed: ' + (err.response?.data?.error || err.message));
    }
    setSubmitting(false);
  };

  const reset = () => { setLr(''); setLrSet(false); setBatch([]); setSubmitted(null); };
  const validCount = batch.filter(b => b.status === 'ok').length;

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <div style={{ ...card, background: 'linear-gradient(135deg,#1e3a5f,#1e40af)', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>📦</span>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Receiving</div>
            <div style={{ color: '#93c5fd', fontSize: 12 }}>{user.name}</div>
          </div>
          {lrSet && <span style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '3px 10px', color: '#fff', fontSize: 13, fontWeight: 700 }}>LR: {lr}</span>}
        </div>
      </div>

      {!lrSet ? (
        <div style={card}>
          <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 14 }}>Enter LR number from the consignment note before scanning.</div>
          <input style={{ ...inp, fontSize: 18, fontWeight: 700, marginBottom: 12 }}
            value={lr} onChange={e => setLr(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && lr.trim() && setLrSet(true)}
            placeholder="e.g. LR20240001" autoCapitalize="characters" />
          <button style={btn('linear-gradient(135deg,#3b82f6,#06b6d4)', !lr.trim())}
            onClick={() => setLrSet(true)} disabled={!lr.trim()}>
            Confirm LR → Start Scanning
          </button>
        </div>
      ) : (
        <>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Scan pieces</span>
              {validCount > 0 && <Tag color="#34d399">{validCount} ready</Tag>}
            </div>
            {!submitted && (
              <BarcodeScanner onScan={addBarcode} active={!submitting}
                scannedCodes={batch.map(b => b.barcode)} />
            )}
            <ManualEntry onAdd={addBarcode} btnColor="#3b82f6" />
          </div>

          {batch.length > 0 && (
            <div style={{ ...card, maxHeight: 260, overflowY: 'auto' }}>
              <div style={{ color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>SCANNED ({batch.length})</div>
              {batch.map((b, i) => (
                <div key={b.barcode + i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 8, marginBottom: 5,
                  background: b.status === 'dup' || b.status === 'error' ? 'rgba(239,68,68,0.08)'
                    : b.status === 'saved' ? 'rgba(5,150,105,0.08)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${b.status === 'dup' || b.status === 'error' ? 'rgba(239,68,68,0.25)'
                    : b.status === 'saved' ? 'rgba(5,150,105,0.25)' : 'rgba(255,255,255,0.06)'}`
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#fff', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{b.barcode}</div>
                    <div style={{ fontSize: 11, color: b.status === 'dup' || b.status === 'error' ? '#fca5a5' : b.status === 'saved' ? '#34d399' : '#94a3b8' }}>{b.msg}</div>
                  </div>
                  {b.status === 'ok' && (
                    <button onClick={() => setBatch(p => p.filter(x => x.barcode !== b.barcode))}
                      style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {submitted && (
            <div style={{ ...card, background: 'rgba(5,150,105,0.12)', border: '1px solid #059669' }}>
              <div style={{ color: '#34d399', fontWeight: 700, fontSize: 15 }}>✅ {submitted.saved} barcodes saved!</div>
              {submitted.errorCount > 0 && <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 4 }}>{submitted.errorCount} errors</div>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn('transparent', false), flex: 1, border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8' }} onClick={reset}>← New LR</button>
            {!submitted ? (
              <button style={{ ...btn('linear-gradient(135deg,#16a34a,#059669)', submitting || validCount === 0), flex: 2 }}
                onClick={submit} disabled={submitting || validCount === 0}>
                {submitting ? 'Saving…' : `Submit ${validCount} to Database`}
              </button>
            ) : (
              <button style={{ ...btn('linear-gradient(135deg,#3b82f6,#06b6d4)', false), flex: 2 }} onClick={reset}>
                New LR Batch
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── QC PAGE ──────────────────────────────────────────────────────────────
export function QCPage({ user }) {
  const [decision, setDecision] = useState('');
  const [decisionSet, setDecisionSet] = useState(false);
  const [batch, setBatch] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const DECISIONS = [
    { id: 'fresh',    icon: '✅', label: 'Fresh',    sub: 'Auto-moves to Inventory', from: '#065f46', to: '#059669', accent: '#34d399' },
    { id: 'refinish', icon: '🔧', label: 'Refinish',  sub: 'Needs repair — partner scans', from: '#7c2d12', to: '#c2410c', accent: '#fb923c' },
    { id: 'q3',       icon: '📦', label: 'Q3',        sub: 'Grade Q3 — disposed', from: '#1e3a8a', to: '#2563eb', accent: '#60a5fa' },
  ];

  const dc = DECISIONS.find(d => d.id === decision) || { from: '#4c1d95', to: '#6d28d9', accent: '#a78bfa' };

  const addBarcode = useCallback(async (code) => {
    const barcode = code.trim().toUpperCase();
    if (!barcode.startsWith('WH')) {
      setBatch(p => [{ barcode, status: 'error', msg: '🚫 Invalid — only WH barcodes accepted' }, ...p.filter(b => b.barcode !== barcode)]);
      return;
    }
    if (batch.find(b => b.barcode === barcode)) {
      setBatch(p => [{ barcode, status: 'dup', msg: '⚠️ Already in this batch' }, ...p.filter(b => b.barcode !== barcode)]);
      return;
    }
    try {
      const res = await scanAPI.validate(barcode);
      if (res.data.exists && res.data.status !== 'RECEIVED') {
        setBatch(p => [{ barcode, status: 'dup', msg: `⚠️ Already processed (${res.data.status})` }, ...p]);
        return;
      }
      if (!res.data.exists) {
        setBatch(p => [{ barcode, status: 'error', msg: '❌ Not received yet' }, ...p]);
        return;
      }
    } catch {}
    setBatch(p => [{ barcode, status: 'ok', msg: '✅ Ready' }, ...p]);
  }, [batch]);

  const submit = async () => {
    const valid = batch.filter(b => b.status === 'ok').map(b => b.barcode);
    if (!valid.length) return;
    setSubmitting(true);
    try {
      const res = await scanAPI.qcBatch(valid, decision);
      setSubmitted(res.data);
      const msg = { fresh: '✅ Moved to Inventory', refinish: '🔧 Marked for Refinishing', q3: '📦 Marked as Q3' }[decision];
      setBatch(p => p.map(b => {
        const err = res.data.errors?.find(e => e.barcode === b.barcode);
        return { ...b, status: err ? 'error' : 'saved', msg: err ? `❌ ${err.error}` : msg };
      }));
    } catch (err) {
      alert('Submit failed: ' + (err.response?.data?.error || err.message));
    }
    setSubmitting(false);
  };

  const reset = () => { setDecision(''); setDecisionSet(false); setBatch([]); setSubmitted(null); };
  const validCount = batch.filter(b => b.status === 'ok').length;

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <div style={{ ...card, background: `linear-gradient(135deg,${dc.from},${dc.to})`, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>🔍</span>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Quality Check</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{user.name}</div>
          </div>
          {decisionSet && (
            <div style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '4px 12px', color: '#fff', fontWeight: 800, fontSize: 13 }}>
              {{ fresh: '✅ FRESH', refinish: '🔧 REFINISH', q3: '📦 Q3' }[decision]}
            </div>
          )}
        </div>
      </div>

      {!decisionSet ? (
        <div style={card}>
          <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Step 1: Select QC Decision</div>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 18 }}>Pick outcome first — then scan all pieces.</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {DECISIONS.map(d => (
              <button key={d.id}
                onClick={() => { setDecision(d.id); setDecisionSet(true); setBatch([]); setSubmitted(null); }}
                style={{ flex: 1, padding: '18px 8px', borderRadius: 14, border: 'none', background: `linear-gradient(135deg,${d.from},${d.to})`, color: '#fff', cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ fontSize: 26, marginBottom: 5 }}>{d.icon}</div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{d.label}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 4, lineHeight: 1.3 }}>{d.sub}</div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Step 2: Scan pieces</span>
              {validCount > 0 && <Tag color={dc.accent || '#fff'}>{validCount} ready</Tag>}
            </div>
            {!submitted && (
              <BarcodeScanner onScan={addBarcode} active={!submitting}
                scannedCodes={batch.map(b => b.barcode)} />
            )}
            <ManualEntry onAdd={addBarcode} btnColor={dc.to || '#6d28d9'} />
          </div>

          {batch.length > 0 && (
            <div style={{ ...card, maxHeight: 260, overflowY: 'auto' }}>
              <div style={{ color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>SCANNED ({batch.length})</div>
              {batch.map((b, i) => (
                <div key={b.barcode + i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 8, marginBottom: 5,
                  background: b.status === 'dup' || b.status === 'error' ? 'rgba(239,68,68,0.08)' : b.status === 'saved' ? 'rgba(5,150,105,0.08)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${b.status === 'dup' || b.status === 'error' ? 'rgba(239,68,68,0.25)' : b.status === 'saved' ? 'rgba(5,150,105,0.25)' : 'rgba(255,255,255,0.06)'}`
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#fff', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{b.barcode}</div>
                    <div style={{ fontSize: 11, color: b.status === 'dup' || b.status === 'error' ? '#fca5a5' : b.status === 'saved' ? '#34d399' : '#94a3b8' }}>{b.msg}</div>
                  </div>
                  {b.status === 'ok' && (
                    <button onClick={() => setBatch(p => p.filter(x => x.barcode !== b.barcode))}
                      style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {submitted && (
            <div style={{ ...card, background: 'rgba(5,150,105,0.12)', border: '1px solid #059669' }}>
              <div style={{ color: '#34d399', fontWeight: 700, fontSize: 15 }}>✅ {submitted.saved} barcodes saved!</div>
              <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                {decision === 'fresh' ? 'Fresh items moved to Inventory.' : decision === 'q3' ? 'Q3 items disposed.' : 'Refinish items waiting for partner.'}
              </div>
              {submitted.errorCount > 0 && <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 4 }}>{submitted.errorCount} errors</div>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn('transparent', false), flex: 1, border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8' }} onClick={reset}>← Change</button>
            {!submitted ? (
              <button style={{ ...btn(`linear-gradient(135deg,${dc.from},${dc.to})`, submitting || validCount === 0), flex: 2 }}
                onClick={submit} disabled={submitting || validCount === 0}>
                {submitting ? 'Saving…' : `Submit ${validCount} as ${dc.label || decision}`}
              </button>
            ) : (
              <button style={{ ...btn('linear-gradient(135deg,#6d28d9,#4f46e5)', false), flex: 2 }} onClick={reset}>
                New QC Batch
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── PARTNER PAGE (Ekart / Reebok / Piyush) ───────────────────────────────
export function PartnerPage({ user }) {
  const partner = (user && user.role) ? user.role : 'ekart';

  const PCOLORS = { ekart: '#b45309', reebok: '#065f46', piyush: '#1e3a8a' };
  const PGRADES = { ekart: '#d97706', reebok: '#059669', piyush: '#2563eb' };
  const color = PGRADES[partner] || '#3b82f6';
  const headerBg = PCOLORS[partner] || '#1e3a8a';
  const cap = s => (s && s.length > 0) ? s.charAt(0).toUpperCase() + s.slice(1) : s;

  const [grade, setGrade] = useState('');
  const [gradeSet, setGradeSet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [sessionCount, setSessionCount] = useState(0);
  const [camActive, setCamActive] = useState(false);
  const sessionScanned = useRef([]);

  const handleScan = useCallback(async (code) => {
    if (loading) return;
    const barcode = code.trim().toUpperCase();
    if (!barcode.startsWith('WH')) {
      setLastResult({ type: 'error', barcode, message: '🚫 Invalid barcode — only WH barcodes accepted' });
      return;
    }
    setLoading(true);
    setCamActive(false);
    try {
      const res = await scanAPI.partner(barcode, grade);
      sessionScanned.current.push(barcode);
      setLastResult({
        type: 'success', barcode,
        message: grade === 'Q1' ? `✅ Q1 — Inventory by ${cap(partner)}` : `📦 Q3 — Q3 by ${cap(partner)}`,
        finalStatus: res.data.finalStatus
      });
      setSessionCount(c => c + 1);
    } catch (err) {
      setLastResult({ type: 'error', barcode, message: '❌ ' + (err.response?.data?.error || err.message) });
    }
    setLoading(false);
    setTimeout(() => setCamActive(true), 1500);
  }, [loading, grade, partner]);

  const selectGrade = (g) => { setGrade(g); setGradeSet(true); setCamActive(true); };
  const changeGrade = () => { setGrade(''); setGradeSet(false); setCamActive(false); setLastResult(null); sessionScanned.current = []; };

  const gradeColors = {
    Q1: { from: '#065f46', to: '#059669' },
    Q3: { from: '#1e3a8a', to: '#2563eb' },
  };
  const gc = gradeColors[grade] || { from: headerBg, to: color };

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>

      {/* Header */}
      <div style={{
        ...card,
        background: gradeSet
          ? `linear-gradient(135deg,${gc.from},${gc.to})`
          : `linear-gradient(135deg,${headerBg},${color})`,
        padding: '16px 20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>🏭</span>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
              {cap(partner)} · Partner Scan
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{user.name}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {gradeSet && grade && (
              <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '3px 12px', color: '#fff', fontWeight: 800, fontSize: 15 }}>
                {grade}
              </span>
            )}
            {sessionCount > 0 && (
              <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '3px 10px', color: '#fff', fontSize: 12 }}>
                {sessionCount} scanned
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Step 1 — Select Grade */}
      {!gradeSet ? (
        <div style={card}>
          <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Select Grade</div>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 18 }}>
            All pieces scanned this session will be assigned this grade. Each scan is final.
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => selectGrade('Q1')} style={{
              flex: 1, padding: '22px 12px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg,#065f46,#059669)',
              color: '#fff', cursor: 'pointer', textAlign: 'center'
            }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>✅</div>
              <div style={{ fontWeight: 900, fontSize: 20 }}>Q1</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 6 }}>Goes to Inventory</div>
            </button>
            <button onClick={() => selectGrade('Q3')} style={{
              flex: 1, padding: '22px 12px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg,#1e3a8a,#2563eb)',
              color: '#fff', cursor: 'pointer', textAlign: 'center'
            }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>📦</div>
              <div style={{ fontWeight: 900, fontSize: 20 }}>Q3</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 6 }}>Disposed / Q3</div>
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Step 2 — Scan */}
          <div style={card}>
            <div style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 10 }}>
              Scan pieces — each scan is <strong style={{ color: gc.to }}>final</strong>
            </div>
            {loading
              ? <div style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>Processing…</div>
              : (
                <>
                  <BarcodeScanner onScan={handleScan} active={camActive} scannedCodes={[...sessionScanned.current]} />
                  <ManualEntry onAdd={handleScan} btnColor={gc.to || color} />
                </>
              )
            }
          </div>

          {/* Last result */}
          {lastResult && (
            <div style={{
              ...card,
              background: lastResult.type === 'success' ? 'rgba(5,150,105,0.12)' : 'rgba(239,68,68,0.12)',
              border: `1px solid ${lastResult.type === 'success' ? '#059669' : '#dc2626'}`
            }}>
              <div style={{ fontFamily: 'monospace', color: '#64748b', fontSize: 11, marginBottom: 4 }}>{lastResult.barcode}</div>
              <div style={{ color: lastResult.type === 'success' ? '#34d399' : '#fca5a5', fontWeight: 700, fontSize: 14 }}>
                {lastResult.message}
              </div>
              {lastResult.finalStatus && (
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                  Final Status: <span style={{ color: '#e2e8f0' }}>{lastResult.finalStatus}</span>
                </div>
              )}
            </div>
          )}

          {/* Change grade */}
          <button onClick={changeGrade} style={{ ...btn('transparent', false), border: '1px solid rgba(255,255,255,0.1)', color: '#64748b' }}>
            ↩ Change Grade
          </button>
        </>
      )}
    </div>
  );
}

// ─── ADMIN PAGE ───────────────────────────────────────────────────────────
export function AdminPage({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lrFilter, setLrFilter] = useState('');

  const loadStats = async () => {
    setLoading(true);
    try { setStats((await scanAPI.getStats()).data); } catch {}
    setLoading(false);
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('wh_token');
      const lr = lrFilter.trim();
      const apiUrl = process.env.REACT_APP_API_URL || 'https://warehousescan-production.up.railway.app';

      // Build direct download URL with token in query param
      // This works in APK because it's a real URL, not a blob URL
      const directUrl = `${apiUrl}/api/scan/export-direct?token=${token}${lr ? '&lr=' + lr : ''}`;

      // Open directly — Android will handle it with DownloadManager
      window.open(directUrl, '_blank');

    } catch (err) {
      alert('❌ Export failed: ' + err.message);
    }
    setExporting(false);
  };

  const statItems = stats ? [
    { label: 'Total Barcodes',  value: stats.total,          color: '#60a5fa' },
    { label: 'Pending QC',      value: stats.received,       color: '#fbbf24' },
    { label: 'In Inventory',    value: stats.inInventory,    color: '#4ade80' },
    { label: 'Pending Partner', value: stats.pendingPartner, color: '#fb923c' },
    { label: 'Q3 Disposed',     value: stats.q3Disposed,     color: '#c084fc' },
  ] : [];

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <div style={{ ...card, background: 'linear-gradient(135deg,#0f172a,#1e293b)', padding: '16px 20px', border: '1px solid rgba(255,255,255,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>⚙️</span>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Admin Dashboard</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>{user.name}</div>
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Live Stats</span>
          <button onClick={loadStats} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', color: '#94a3b8', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 13 }}>
            {loading ? '…' : 'Refresh'}
          </button>
        </div>
        {!stats && !loading && <div style={{ color: '#475569', fontSize: 14 }}>Press Refresh to load stats</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {statItems.map(s => (
            <div key={s.label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ color: s.color, fontSize: 22, fontWeight: 800 }}>{s.value}</div>
              <div style={{ color: '#64748b', fontSize: 12 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 10 }}>📥 Export to Excel</div>
        <input style={{ ...inp, marginBottom: 10 }} value={lrFilter}
          onChange={e => setLrFilter(e.target.value.toUpperCase())}
          placeholder="Filter by LR number (optional — blank = all data)" />

        {/* Main download button */}
        <button style={btn('linear-gradient(135deg,#16a34a,#059669)', exporting)} onClick={exportExcel} disabled={exporting}>
          {exporting ? 'Generating Excel…' : '⬇️ Download Excel (.xlsx)'}
        </button>

        <div style={{ color: '#475569', fontSize: 12, marginTop: 8, marginBottom: 14 }}>
          After downloading → check notification bar or Downloads folder
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 14 }}>
          <div style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            📱 Can't find the file on phone?
          </div>
          <div style={{ color: '#64748b', fontSize: 12, lineHeight: 1.8 }}>
            <div>1. Open <strong style={{ color: '#94a3b8' }}>Google Sheets</strong> app → tap + → Open → Browse → Downloads</div>
            <div>2. Open <strong style={{ color: '#94a3b8' }}>Files / My Files</strong> app → Internal Storage → Downloads</div>
            <div>3. Open <strong style={{ color: '#94a3b8' }}>Chrome</strong> → tap 3 dots → Downloads → tap the file</div>
          </div>
        </div>

        {/* Best option for mobile */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 14, marginTop: 4 }}>
          <div style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            💡 Best way on mobile:
          </div>
          <div style={{ color: '#64748b', fontSize: 12, lineHeight: 1.6 }}>
            Open this app on your <strong style={{ color: '#94a3b8' }}>laptop/computer</strong> → login as admin → download Excel → file opens directly in Microsoft Excel
          </div>
        </div>
      </div>
    </div>
  );
}
